import React from 'react';
import { Loader2, X, UserPlus, Briefcase, Search } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';

interface TypeEmployee {
    link_id: string;
    employee_id: string;
    first_name: string;
    last_name: string;
}

interface ExtraType {
    id: string;
    label: string;
    partner_name: string;
    employees: TypeEmployee[];
}

interface Employee {
    id: string;
    first_name?: string;
    last_name?: string;
}

/** Paramètres > Événementiel > "Extras internes" : pas de liste de postes à créer soi-même — on choisit
 *  directement parmi les types d'extras déjà créés dans "Partenaires Extras" (tous partenaires confondus) et
 *  on y rattache des salariés, pour pouvoir booker un extra avec l'un de ses propres salariés en plus des
 *  partenaires externes — demande explicite d'Emeni, 2026-08-19, de ne pas dupliquer ce vocabulaire. */
export function ExtrasInternalTypesSettings({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [types, setTypes] = React.useState<ExtraType[]>([]);
    const [employees, setEmployees] = React.useState<Employee[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [error, setError] = React.useState('');
    const [addEmployeeFor, setAddEmployeeFor] = React.useState<string | null>(null);
    const [employeeSearch, setEmployeeSearch] = React.useState('');

    const load = React.useCallback(() => {
        setLoading(true);
        Promise.all([
            moduleApi.getExtrasInternalTypes().catch(() => []),
            moduleApi.getEmployes().catch(() => []),
        ]).then(([t, emp]) => {
            setTypes(Array.isArray(t) ? t : []);
            setEmployees(Array.isArray(emp) ? emp : []);
        }).finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { if (open) load(); }, [open, load]);

    const employeeLabel = (e: Employee) => `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'Salarié';

    const handleAddEmployee = async (extraTypeId: string, employeeId: string) => {
        try {
            await moduleApi.addExtrasInternalTypeEmployee(extraTypeId, employeeId);
            setAddEmployeeFor(null);
            setEmployeeSearch('');
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible d'ajouter ce salarié.");
        }
    };

    const handleRemoveEmployee = async (extraTypeId: string, employeeId: string) => {
        try {
            await moduleApi.removeExtrasInternalTypeEmployee(extraTypeId, employeeId);
            load();
        } catch (e: any) {
            setError(e?.message || 'Impossible de retirer ce salarié.');
        }
    };

    const qLower = q.trim().toLowerCase();
    const filteredTypes = types.filter((t) => !qLower || t.label.toLowerCase().includes(qLower) || t.partner_name.toLowerCase().includes(qLower));

    return (
        <Modal open={open} onClose={onClose} title="Extras internes" icon={<Briefcase size={16} />} maxWidth="max-w-xl"
            footer={<Button variant="secondary" onClick={onClose}>Fermer</Button>}>
            <div className="space-y-4">
                <p className="text-xs text-content-muted">
                    Choisis parmi tes types d'extras déjà créés dans "Partenaires Extras" ceux que tes salariés peuvent aussi occuper, et rattache-leur des salariés.
                </p>

                <div className="relative">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un type..." className="pl-8" />
                </div>

                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

                {loading ? (
                    <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                ) : types.length === 0 ? (
                    <p className="text-sm text-content-muted">Aucun type d'extra pour l'instant — crées-en d'abord depuis "Partenaires Extras".</p>
                ) : filteredTypes.length === 0 ? (
                    <p className="text-sm text-content-muted">Aucun type ne correspond.</p>
                ) : (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                        {filteredTypes.map((t) => {
                            const attachedIds = new Set(t.employees.map((e) => e.employee_id));
                            const available = employees.filter((e) => !attachedIds.has(e.id));
                            return (
                                <div key={t.id} className="bg-surface-soft border border-border rounded-xl p-3.5 space-y-2.5">
                                    <div>
                                        <h4 className="text-sm font-bold text-content">{t.label}</h4>
                                        <p className="text-[10px] text-content-muted uppercase tracking-widest">{t.partner_name}</p>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {t.employees.map((e) => (
                                            <span key={e.link_id} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-surface border border-border rounded-full pl-2.5 pr-1 py-1 text-content">
                                                {`${e.first_name} ${e.last_name}`.trim() || 'Salarié'}
                                                <button onClick={() => handleRemoveEmployee(t.id, e.employee_id)} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400">
                                                    <X size={11} />
                                                </button>
                                            </span>
                                        ))}
                                        {t.employees.length === 0 && <span className="text-[11px] text-content-muted italic">Aucun salarié rattaché.</span>}
                                    </div>
                                    {addEmployeeFor === t.id ? (
                                        <div className="space-y-1.5">
                                            <div className="flex items-center gap-1.5">
                                                <div className="relative flex-1 min-w-0">
                                                    <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-content-muted" />
                                                    <input value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)} autoFocus
                                                        placeholder="Rechercher un salarié..."
                                                        className="w-full bg-surface border border-border rounded-md pl-7 pr-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                                </div>
                                                <Button size="sm" variant="secondary" onClick={() => { setAddEmployeeFor(null); setEmployeeSearch(''); }}><X size={12} /></Button>
                                            </div>
                                            <div className="max-h-40 overflow-y-auto space-y-1">
                                                {available
                                                    .filter((e) => !employeeSearch.trim() || employeeLabel(e).toLowerCase().includes(employeeSearch.trim().toLowerCase()))
                                                    .map((e) => (
                                                        <button key={e.id} onClick={() => handleAddEmployee(t.id, e.id)}
                                                            className="w-full flex items-center justify-between gap-2 text-left px-2.5 py-1.5 rounded-md text-xs text-content hover:bg-surface transition-colors">
                                                            {employeeLabel(e)}
                                                            <UserPlus size={12} className="text-content-muted shrink-0" />
                                                        </button>
                                                    ))}
                                                {available.filter((e) => !employeeSearch.trim() || employeeLabel(e).toLowerCase().includes(employeeSearch.trim().toLowerCase())).length === 0 && (
                                                    <p className="text-[11px] text-content-muted italic px-2.5 py-1">Aucun salarié ne correspond.</p>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={() => setAddEmployeeFor(t.id)} disabled={available.length === 0}
                                            className="flex items-center gap-1 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-40 disabled:no-underline">
                                            <UserPlus size={12} /> Rattacher un salarié
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Modal>
    );
}
